import omit from 'lodash/omit';
import { identity, mergeAll, apply } from 'ramda';
import { call, select, all, take, put, takeLatest } from 'redux-saga/effects';

import { SignupApi } from '../../../infrastructure/api';
import {
  setPrefillStatus,
  INIT_SIGNUP,
  prefillFields,
  SET_IDENTITY_PROVIDER_TOKEN,
  submitStep,
  track,
} from '../actions';
import {
  defaultLanguage,
  defaultCountry,
  defaultCurrency,
  defaultTimeZone,
  STEPS_ORDER,
  STEP_PROFILE,
  STEP_REGION,
} from '../constants';
import {
  selectCompanyFormParameter,
  selectCompanySizeParameter,
  selectCountry,
  selectEmailParameter,
  selectHasEInvoicingEnabled,
  selectIdentityProviderToken,
  selectLocationQuery,
  selectSignupFeaturesParameter,
} from '../selectors';
import { PrefillStatus } from '../types';
import {
  validateLanguageCode,
  validateCountryCode,
  validateCurrency,
  validateTimeZone,
  getBrowserLanguage,
  getBrowserTimeZone,
} from '../util';

import { getSignupDataFromLocalStorage } from './localStorage';

const cacheReturnValue = (fn) => {
  let didCache = false;
  let cachedValue;

  return function* (...args) {
    if (didCache === false) {
      cachedValue = yield call(fn, ...args);
      didCache = true;
    }
    return cachedValue;
  };
};

export const getValuesFromLocalStorage = function* () {
  const hasEInvoicingEnabled = yield select(selectHasEInvoicingEnabled);

  const { steps, maxStepIndexSeen } = yield call(getSignupDataFromLocalStorage);
  // only take into account steps that were seen by the user.
  const stepNames = STEPS_ORDER.slice(0, maxStepIndexSeen + 1);

  return mergeAll(
    stepNames.map((stepName) => {
      const step = steps[stepName];

      if (hasEInvoicingEnabled && stepName === STEP_REGION) {
        return omit(step, 'country');
      }

      if (!hasEInvoicingEnabled && stepName === STEP_PROFILE) {
        return omit(step, 'country');
      }

      return step;
    }),
  );
};

export const getValuesFromIdentityProviderToken = function* () {
  const token = yield select(selectIdentityProviderToken);

  if (token) {
    return {
      email: token.email ?? undefined,
      firstName: token.firstname ?? undefined,
      lastName: token.lastname ?? undefined,
      telephone: token.phone ?? undefined,
    };
  }

  return {};
};

export const cachedGetValuesFromLocalStorage = cacheReturnValue(getValuesFromLocalStorage);

export const getRegionSuggestionsBasedOnIp = (() => {
  let promise;

  return () => {
    if (!promise) {
      promise = new Promise((resolve) => {
        SignupApi.regionSettings.suggest().then(
          (response) => {
            resolve(response.data || {});
          },
          () => {
            resolve({});
          },
        );
      });
    }

    return promise;
  };
})();

export const getValuesFromIpInformation = function* () {
  const { country, timeZone, currency, language } = yield call(getRegionSuggestionsBasedOnIp);

  return {
    country,
    timeZone,
    currency,
    language,
    telephoneCountry: country,
  };
};

export const getValuesFromQueryParameters = function* () {
  const queryParams = (yield select(selectLocationQuery)) || {};

  const email = yield select(selectEmailParameter);
  const companyForm = yield select(selectCompanyFormParameter);
  const companySize = yield select(selectCompanySizeParameter);
  const signupFeatures = yield select(selectSignupFeaturesParameter);

  return {
    email,
    language: queryParams.lang,
    telephoneCountry: queryParams.country,
    companyForm,
    companySize,
    signupFeatures,
    ...queryParams,
  };
};

export const getValuesFromBrowserSettings = function* () {
  const timeZone = yield call(getBrowserTimeZone);
  let language = yield call(getBrowserLanguage);

  if (language.indexOf(defaultLanguage) >= 0) {
    // don't use the browser language if it's equal to the default language
    language = undefined;
  }

  return {
    language,
    timeZone,
  };
};

export const getDefaultValues = function* () {
  return {
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyLookup: undefined,
    companyForm: undefined,
    companySize: undefined,
    vertical: undefined,
    telephone: '',
    country: defaultCountry,
    telephoneCountry: defaultCountry,
    timeZone: defaultTimeZone,
    language: defaultLanguage,
    currency: defaultCurrency,
    signupFeatures: [],
  };
};

export const async = (fn) => {
  const wrapper = function* (...args) {
    return yield call(fn, ...args);
  };

  wrapper.__async = true;

  return wrapper;
};

export const createInitialValueGetter = (fieldName, validateValue, valueGetters) =>
  function* (skipAsync) {
    for (let i = 0; i < valueGetters.length; i += 1) {
      const valueGetter = valueGetters[i];

      if (skipAsync && valueGetter.__async) {
        continue;
      }

      const getterValue = (yield call(valueGetters[i]))[fieldName];
      const validatedValue = getterValue !== undefined ? yield call(validateValue, getterValue) : undefined;

      if (validatedValue !== undefined) {
        return {
          [fieldName]: validatedValue,
        };
      }
    }

    return {};
  };

export const initialValueGetters = [
  [
    'email',
    identity,
    [
      getValuesFromQueryParameters,
      getValuesFromIdentityProviderToken,
      cachedGetValuesFromLocalStorage,
      getDefaultValues,
    ],
  ],
  ['firstName', identity, [getValuesFromIdentityProviderToken, cachedGetValuesFromLocalStorage, getDefaultValues]],
  ['lastName', identity, [getValuesFromIdentityProviderToken, cachedGetValuesFromLocalStorage, getDefaultValues]],
  ['companyName', identity, [getValuesFromIdentityProviderToken, cachedGetValuesFromLocalStorage, getDefaultValues]],
  ['companyLookup', identity, [cachedGetValuesFromLocalStorage, getDefaultValues]],
  ['vertical', identity, [cachedGetValuesFromLocalStorage, getDefaultValues]],
  [
    'companyForm',
    identity,
    [
      getValuesFromQueryParameters,
      getValuesFromIdentityProviderToken,
      cachedGetValuesFromLocalStorage,
      getDefaultValues,
    ],
  ],
  [
    'companySize',
    identity,
    [
      getValuesFromQueryParameters,
      getValuesFromIdentityProviderToken,
      cachedGetValuesFromLocalStorage,
      getDefaultValues,
    ],
  ],
  ['telephone', identity, [getValuesFromIdentityProviderToken, cachedGetValuesFromLocalStorage, getDefaultValues]],
  [
    'telephoneCountry',
    validateCountryCode,
    [
      cachedGetValuesFromLocalStorage,
      async(getValuesFromIpInformation),
      getValuesFromQueryParameters,
      getDefaultValues,
    ],
  ],
  [
    'country',
    validateCountryCode,
    [
      cachedGetValuesFromLocalStorage,
      async(getValuesFromIpInformation),
      getValuesFromQueryParameters,
      getDefaultValues,
    ],
  ],
  [
    'language',
    validateLanguageCode,
    [
      cachedGetValuesFromLocalStorage,
      getValuesFromQueryParameters,
      getValuesFromBrowserSettings,
      async(getValuesFromIpInformation),
      getDefaultValues,
    ],
  ],
  [
    'timeZone',
    validateTimeZone,
    [
      cachedGetValuesFromLocalStorage,
      async(getValuesFromIpInformation),
      getValuesFromBrowserSettings,
      getDefaultValues,
    ],
  ],
  [
    'currency',
    validateCurrency,
    [cachedGetValuesFromLocalStorage, async(getValuesFromIpInformation), getDefaultValues],
  ],
  ['signupFeatures', identity, [getValuesFromQueryParameters, cachedGetValuesFromLocalStorage, getDefaultValues]],
].map(apply(createInitialValueGetter));

export const getFieldPrefillValues = function* (skipAsync) {
  const values = mergeAll(yield all(initialValueGetters.map((getter) => call(getter, skipAsync))));

  yield put(setPrefillStatus(skipAsync ? PrefillStatus.Synchronous : PrefillStatus.Asynchronous));

  return values;
};

export function* handleSetIdentityProviderToken() {
  const idpToken = yield select(selectIdentityProviderToken);
  const country = yield select(selectCountry);

  if (idpToken && idpToken.email) {
    yield put(submitStep({ stepIndex: 0 }));
    yield put(
      track({
        event: 'signupWizard.loginInfoSubmitted',
        source: 'signupWizard',
        meta: {
          country,
        },
      }),
    );
  }
}

export default function* prefillFormSaga() {
  yield takeLatest(SET_IDENTITY_PROVIDER_TOKEN, handleSetIdentityProviderToken);

  yield take(INIT_SIGNUP);

  // first skip async field prefillers, so we can immediately seed the form without having to wait for request responses
  yield put(prefillFields(yield call(getFieldPrefillValues, true)));
  yield put(prefillFields(yield call(getFieldPrefillValues)));
}
